import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, SearchX } from 'lucide-react';
import { exportToCSV } from '../utils/helpers';

/**
 * Table — reusable data table with pagination, export, and empty state.
 *
 * The search/filter bar is intentionally removed from here — each page
 * uses <FilterBar> to own filtering, then passes the already-filtered
 * array as `data`.  The old `searchKeys` prop is kept for backward
 * compatibility but is no-op when FilterBar is used.
 */
const Table = ({
  columns,
  data = [],
  loading,
  title,
  actions,
  exportFilename,
  serverPagination,
}) => {
  const [page, setPage] = useState(1);
  const perPage = 10;

  // Inventory.jsx ke top mein (baaki useState ke saath)
const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages]   = useState(1);
const [totalItems, setTotalItems]   = useState(0);

  // Reset to page 1 whenever the dataset changes (e.g. after a filter)
  useEffect(() => {
    setPage(1);
  }, [data.length]);

  // const pages = serverPagination ? 0 : Math.ceil(data.length / perPage);
  // const paginated = serverPagination
  //   ? data
  //   : data.slice((page - 1) * perPage, page * perPage);

      const isServer = !!serverPagination;
      const pages = isServer
        ? serverPagination.totalPages
        : Math.ceil(data.length / perPage);
      const currentP = isServer ? serverPagination.page : page;
      const paginated = isServer
        ? data
        : data.slice((page - 1) * perPage, page * perPage);

      const handlePageChange = (p) => {
        if (isServer) serverPagination.onPageChange(p);
        else setPage(p);
      };

  return (
    <div className="card overflow-hidden">
      {/* ── Header ── */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        <div className="flex items-center gap-2 w-full sm:w-auto overflow-hidden">
          {exportFilename && (
            <button
              onClick={() => exportToCSV(data, exportFilename)}
              className="btn-secondary flex items-center gap-1.5 whitespace-nowrap text-sm"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
          <div className="flex-1 sm:flex-none overflow-x-auto">{actions}</div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400 dark:text-gray-500">
                    <SearchX className="w-10 h-10 opacity-40" />
                    <p className="text-sm font-medium">No data found</p>
                    <p className="text-xs">
                      Try adjusting your search or filters
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <tr
                  key={row.id || i}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300"
                    >
                      {col.render
                        ? col.render(row[col.key], row)
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span className="text-xs">
            Showing {(currentP - 1) * perPage + 1}– {/* ✅ page → currentP */}
            {isServer
              ? Math.min(currentP * perPage, serverPagination.total) // ✅ server total
              : Math.min(currentP * perPage, data.length)}{" "}
            of {isServer ? serverPagination.total : data.length}{" "}
            {/* ✅ server total */}
          </span>
          <div className="flex items-center gap-1">
            <button
              // onClick={() => setPage((p) => Math.max(1, p - 1))}
              onClick={() => handlePageChange(currentP - 1)}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              const p =
                pages <= 5
                  ? i + 1
                  : currentP <= 3 // ✅ page → currentP
                    ? i + 1
                    : currentP >= pages - 2 // ✅ page → currentP
                      ? pages - 4 + i
                      : currentP - 2 + i;
              return (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)} // ✅ setPage(p) → handlePageChange(p)
                  className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                    currentP === p // ✅ page === p → currentP === p
                      ? "bg-medihub-primary text-white"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              // onClick={() => setPage((p) => Math.min(pages, p + 1))}
              onClick={() => handlePageChange(currentP + 1)}
              disabled={page === pages}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
